import { eventService } from "../_services"
import moment from '@/common/AppMoment.js'

let calendarType = {
  MONTH:      { id: 1, name: "месяц",         isMonth: true },
  FULL_WEEK:  { id: 2, name: "7 дней недели", isWeek: true,  days: 7 },
  WORK_WEEK:  { id: 3, name: "5 дней недели", isWeek: true,  days: 5 }
}

const calendarDateFormat = "YYYY-MM-DD"

/**
 * ВАЖНО! 
 * 
 * Даты в хранилище хранятся в виде строк формата "calendarDateFormat" (date, today, period.date),
 * так как система реактивности по умолчанию не отслеживает изменение внутреннего состояния сложного объекта (moment()),
 * а глубокое сканирование/слежение потребляет много ресурсов.
 * 
 * В связи с этим, для большей прозрачности взаимодействие внешних объектов с полями, содержащими дату, так же производится в строковом формате "calendarDateFormat"
 */

export default {
  namespaced: true,

  state: {
    // Фильтр по тегу - выбранный тег
    tag: null,
    period: {
      // Вид календаря - месяц, неделя 7 дней, неделя 5 дней
      type: calendarType.MONTH,
      // Базовая дата, взятая за основу для расчета периода и загрузки событий - выбирается через DatePicker в CalendarToolbar
      date: moment().format(calendarDateFormat),
    },
    // Текущая дата - используется для сброса поля 'date', если наступил новый день
    today: moment().format(calendarDateFormat),
    // Выбранная дата / день - выбирается в самом календаре (месяц / неделя), используется для отображения событий на указаный день
    selectedDate: moment().format(calendarDateFormat),
    // Текущее редактируемое событие
    selectedEvent: null,
    selectedEventTime: 0,
    // Загружаемые данные
    events: [],
    tags: [],
    // Флаг, указывающий, что идет обновление списка событий
    updating: false,
  },


  getters: {
    filteredEvents: ({ events, tag }) => {
      return events
      .filter(e => !tag || e.eventTags.findIndex(t => t.id == tag.id) != -1)
      .sort((a, b) => a.eventDate.localeCompare(b.eventDate) || a.id.localeCompare(b.id))
    },

    calendarTypes: () => [calendarType.MONTH, calendarType.FULL_WEEK, calendarType.WORK_WEEK],

    // Range периода - начало и конец
    periodStart ({ period }) {
      let base = period.type.isMonth ? moment(period.date).startOf('month') : moment(period.date)
      return base.startOf('week').format(calendarDateFormat)
    },
    // ВАЖНО!
    // periodEnd на 1 день больше реального конца периода для упрощения запросов к серверу - add(1, 'day')
    // periodEnd умышленно раcсчитывается на полную неделю (7 дней), даже если пользователь выбрал тип календаря "5 дней"
    // это сделано с целью не перезапрашивать данные при переключении между 5-ти и 7-ми дневной неделей - данные всегда запрашиваются на полную неделю
    periodEnd: ({ period }) => {
      let base = period.type.isMonth ? moment(period.date).endOf('month') : moment(period.date)
      return base.endOf('week').add(1, 'day').format(calendarDateFormat)
    },
    periodDates: ({ period }, getters) => {
      let list = [], current = moment(getters.periodEnd, calendarDateFormat)
      while (current.isAfter(getters.periodStart)) {
        list.push(current.subtract(1, 'days').format(calendarDateFormat))
      }
      list.reverse()
      return period.type.isMonth ? list : list.slice(0, period.type.days)
    },

    allTags: ({ tags }) => tags,
  },


  mutations: {
    selectEvent: (state, payload) => { 
      state.selectedEvent = payload 
      state.selectedEventTime = moment().valueOf()
    },
    createEmptyEvent: (state) => { 
      state.selectedEvent = {} 
      state.selectedEventTime = moment().valueOf()
    },
    resetEvent: (state) => { state.selectedEvent = null },

    setTag: (state, payload) => { state.tag = payload },
    setPeriodType: (state, payload = calendarType.MONTH) => { 
      state.period.type = payload 
      state.selectedDate = null
    },
    setPeriodDate: (state, payload) => { 
      state.period.date = moment(payload).format(calendarDateFormat) 
      state.selectedDate = null
    },
    setPrevPeriod: (state) => { 
      state.period.date = moment(state.period.date).subtract(1, state.period.type.isMonth ? 'months' : 'weeks').format(calendarDateFormat) 
      state.selectedDate = null
    },
    setNextPeriod: (state) => { 
      state.period.date = moment(state.period.date).add(1, state.period.type.isMonth ? 'months' : 'weeks').format(calendarDateFormat) 
      state.selectedDate = null
    },

    selectDate: (state, payload) => { state.selectedDate = payload && moment(payload).format(calendarDateFormat) },
    resetToday: (state) => { 
      state.today = moment().format(calendarDateFormat) 
      state.selectedDate = moment().format(calendarDateFormat) 
    },

    setUpdating: (state, flag) => { state.updating = flag },

    setTags: (state, payload = []) => {
      state.tags = payload.map(t => {
        t.hashtag = `# ${t.name}`
        return t
      })
    },

    setEvents: (state, payload = []) => { state.events = payload },
    updateEventsByAdd: (state, event) => { state.events.push(event) },
    updateEventsByReplace: (state, event) => { state.events = state.events.map(obj => obj.id === event.id ? event : obj) },
    updateEventsByRemove: (state, eventId) => { state.events = state.events.filter(ev => ev.id !== eventId) },
  },


  actions: {
    init: ({ dispatch, state, commit }) => {
      if (moment().isSame(state.today, 'day') == false) {
        commit('resetToday')
      }
      dispatch('loadTags')
      dispatch('loadEvents')
    },

    loadTags: ({ commit }) => {
      eventService.getEventTags().then(data => {
        commit('setTags', data)
      })
    },

    loadEvents: ({ commit, getters }) => {
      commit('setUpdating', true)
      eventService.getEvents(getters.periodStart, getters.periodEnd).then(data => {
        commit('setEvents', data)
        commit('setUpdating', false)
      })
    },

    // Перезагрузка списка событий при смене типа календаря (месяц / 7 дней / 5 дней)
    reloadEventsWithPeriodType: ({ dispatch }, type) => {
      dispatch('reloadEventsWithMutation', { mutation: 'setPeriodType', value: type })
    },
    
    // Перезагрузка списка событий при выборе новой даты в DatePicker в CalendarToolbar
    reloadEventsWithPeriodDate: ({ dispatch }, date) => {
      dispatch('reloadEventsWithMutation', { mutation: 'setPeriodDate', value: date })
    },
    
    // Проверка: не перегружать список событий, если новый период попадает в текущий Range (начало и конец действующего периода)
    // Например:
    // - при переходе от месяца к неделе, так как неделя попадает в Range месяца и эти данные уже подгружены
    // - при переключении режима недели - 5/7 дней
    reloadEventsWithMutation: ({ commit, dispatch, getters }, { mutation, value }) => {
      let bufferedStart = getters.periodStart, bufferedEnd = getters.periodEnd

      commit(mutation, value)
      if (moment(getters.periodStart).isBefore(bufferedStart) || moment(getters.periodEnd).isAfter(bufferedEnd)) {
        dispatch('loadEvents')
      }
    },

    // Загружаем предыдущий или следующий Range
    loadEventsForPrevPeriod: ({ commit, dispatch }) => {
      commit('setPrevPeriod')
      dispatch('loadEvents')
    },
    
    loadEventsForNextPeriod: ({ commit, dispatch }) => {
      commit('setNextPeriod')
      dispatch('loadEvents')
    },

    // Управление событием - создание, изменение, удаление
    removeEvent: ({ commit }, event) => {
      eventService.removeEvent(event.id).then(() => { commit('updateEventsByRemove', event.id) })
    },

    createEvent: ({ commit, dispatch }, payload) => {
      eventService.createEvent(payload).then(data => { 
        commit('updateEventsByAdd', data)
        dispatch('loadTags')
      })
    },

    editEvent: ({ commit, dispatch }, event) => {
      eventService.changeEvent(event).then(data => { 
        commit('updateEventsByReplace', data)
        dispatch('loadTags')
      })
    }
  }
}